import React, { useEffect, useState } from 'react';
import axios from 'axios';
import Sidebar from '../components/Sidebar';
import Topbar from '../components/Topbar';
import { FaChartPie, FaLayerGroup, FaHourglassHalf, FaUsers, FaUserPlus,   FaFileExcel } from 'react-icons/fa';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChartPie, faChartBar, faUsers, faListAlt, faTicketAlt, faLayerGroup, faUser , faLink} from '@fortawesome/free-solid-svg-icons';
import * as XLSX from 'xlsx';
import { FaLink } from 'react-icons/fa';



// Helper: generate monthly growth data from members
function generateMemberGrowthData(members) {
  if (!Array.isArray(members)) return [];
  // Group by month
  const counts = {};
  members.forEach(m => {
    if (!m.RegDate) return;
    const d = new Date(m.RegDate);
    if (isNaN(d)) return;
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    counts[key] = (counts[key] || 0) + 1;
  });
  // Sort months and accumulate
  const months = Object.keys(counts).sort();
  let cumulative = 0;
  return months.map(month => {
    cumulative += counts[month];
    return { month, count: cumulative };
  });
}

export default function ClubStatistics() {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [totalStudents, setTotalStudents] = useState(0);

  const user = JSON.parse(localStorage.getItem('user')) || {
    name: 'Unknown User',
    role: 'Member',
    image: '/user.png',
  };

  const exportToExcel = (category) => {
    // Prepare data for export
    const firstPrefData = category.firstPref.map((student, index) => ({
      'S.No': index + 1,
      'Name': student.name,
      'Faculty': student.faculty,
      'Year/Semester': student.year,
      'Phone': student.phone,
      'Preference': student.pref,
      'Type': 'First Preference'
    }));

    const secondPrefData = category.secondPref.map((student, index) => ({
      'S.No': firstPrefData.length + index + 1,
      'Name': student.name,
      'Faculty': student.faculty,
      'Year/Semester': student.year,
      'Phone': student.phone,
      'Preference': student.pref,
      'Type': 'Second Preference'
    }));

    // Combine both preferences
    const data = [...firstPrefData, ...secondPrefData];

    // Create workbook and worksheet
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(data);

    // Set column widths
    const wscols = [
      { wch: 5 },  // S.No
      { wch: 25 }, // Name
      { wch: 30 }, // Faculty
      { wch: 15 }, // Year/Semester
      { wch: 15 }, // Phone
      { wch: 30 }, // Preference
      { wch: 15 }, // Type
    ];
    ws['!cols'] = wscols;

    // Add worksheet to workbook
    XLSX.utils.book_append_sheet(wb, ws, 'Category Data');

    // Generate Excel file
    const fileName = `${category.name.replace(/[^\w\s]/gi, '')}_${new Date().toISOString().split('T')[0]}.xlsx`;
    XLSX.writeFile(wb, fileName);
  };

  useEffect(() => {
    const fetchCategoryData = async () => {
      try {
        setLoading(true);
        setError(null);

        // Fetch category data
        const response = await axios.get('https://dev.sliitleo.org/Backend/category_selection.php');
        const data = response.data;

        if (data.success) {
          setCategories(data.data);
          setTotalStudents(data.totalStudents);
        } else {
          throw new Error(data.error);
        }
      } catch (err) {
        setError('Failed to load category data: ' + err.message);
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchCategoryData();
  }, []);

  // Compute statistics
  const now = new Date();
  const total = totalStudents;

  // Registration year-based groups
  const regYears = [];
  const currentYear = now.getFullYear();
  const prevYears = [currentYear, currentYear-1, currentYear-2, currentYear-3];
  const yearCounts = prevYears.map(y => 0);
  const previousYearsCount = 0;

  return (
    <div className="min-h-screen">
      <Sidebar />
      <Topbar user={user} />
      <main className="lg:ml-64 mt-14 w-full px-2 sm:px-4 md:px-6 lg:px-8 py-4 md:py-6 lg:py-10">
        {/* Header */}
        <div className="page-head">
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-4)', minWidth: 0 }}>
            <span style={{
              width: 44, height: 44, borderRadius: 'var(--radius-md)', flexShrink: 0,
              display: 'grid', placeItems: 'center', background: 'var(--accent)', color: '#fff',
            }}>
              <FontAwesomeIcon icon={faLayerGroup} size="lg" />
            </span>
            <div style={{ minWidth: 0 }}>
              <h1 className="truncate">Club Dashboard</h1>
              <p className="page-desc truncate">Category Selection</p>
            </div>
          </div>
        </div>

        {/* Navigation */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 'var(--sp-6)' }}>
          <Link to="/club-statistics" className="btn btn-ghost btn-sm">
            <FontAwesomeIcon icon={faChartPie} /> Club Statistics
          </Link>
          <Link to="/reporting-hub" className="btn btn-ghost btn-sm">
            <FontAwesomeIcon icon={faChartBar} /> Reporting Hub
          </Link>
          <Link to="/member-database" className="btn btn-ghost btn-sm">
            <FontAwesomeIcon icon={faUsers} /> Member Database
          </Link>
          <Link
            to="/category-selection"
            className="btn btn-sm"
            style={{ background: 'rgba(124,58,237,0.16)', border: '1px solid var(--accent)', color: 'var(--text-1)' }}
          >
            <FontAwesomeIcon icon={faListAlt} /> Category Selections
          </Link>
          <Link to="/tickets-admin" className="btn btn-ghost btn-sm">
            <FontAwesomeIcon icon={faTicketAlt} /> Tickets-admin
          </Link>
        </div>

        {error && (
          <div className="error-banner" style={{ marginBottom: 'var(--sp-5)' }}>{error}</div>
        )}

        {loading ? (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 md:gap-5" style={{ marginBottom: 'var(--sp-6)' }}>
              {Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="panel tile">
                  <div className="skeleton" style={{ height: 12, width: 110, marginBottom: 10 }} />
                  <div className="skeleton" style={{ height: 26, width: 64 }} />
                </div>
              ))}
            </div>
            <div className="panel" style={{ padding: 'var(--sp-6)', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10 }}>
              <span className="spinner lg" />
              <span style={{ color: 'var(--text-2)', fontSize: 13 }}>Loading category data…</span>
            </div>
          </>
        ) : (
          <>
            {/* Stats Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 md:gap-5" style={{ marginBottom: 'var(--sp-6)' }}>
              {/* Total Responses */}
              <div className="panel tile">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <div>
                    <div className="tile-label">Total Responses</div>
                    <div className="tile-value num">{total}</div>
                  </div>
                  <span style={{
                    width: 36, height: 36, borderRadius: 'var(--radius-sm)', display: 'grid', placeItems: 'center',
                    background: 'rgba(47,111,237,0.14)', color: 'var(--link)',
                  }}>
                    <FaUsers size={15} />
                  </span>
                </div>
                <div className="tile-trend" style={{ color: 'var(--link)' }}>
                  <span style={{ width: 6, height: 6, borderRadius: 999, background: 'var(--link)' }} />
                  +12% from last month
                </div>
              </div>

              {/* Active Categories */}
              <div className="panel tile">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <div>
                    <div className="tile-label">Active Categories</div>
                    <div className="tile-value num">{categories.length}</div>
                  </div>
                  <span style={{
                    width: 36, height: 36, borderRadius: 'var(--radius-sm)', display: 'grid', placeItems: 'center',
                    background: 'rgba(63,174,106,0.14)', color: 'var(--success)',
                  }}>
                    <FaLayerGroup size={15} />
                  </span>
                </div>
                <div className="tile-trend trend-up">
                  <span style={{ width: 6, height: 6, borderRadius: 999, background: 'var(--success)' }} />
                  5 new this month
                </div>
              </div>

              {/* Inactive Categories */}
              <div className="panel tile">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <div>
                    <div className="tile-label">Inactive Categories</div>
                    <div className="tile-value num">
                      {categories.filter(cat =>
                        (!cat.firstPref || cat.firstPref.length === 0) &&
                        (!cat.secondPref || cat.secondPref.length === 0)
                      ).length}
                    </div>
                  </div>
                  <span style={{
                    width: 36, height: 36, borderRadius: 'var(--radius-sm)', display: 'grid', placeItems: 'center',
                    background: 'rgba(226,100,92,0.14)', color: 'var(--error)',
                  }}>
                    <FaHourglassHalf size={15} />
                  </span>
                </div>
                <div className="tile-trend trend-warn">
                  <span style={{ width: 6, height: 6, borderRadius: 999, background: 'var(--warning)' }} />
                  {categories.some(cat =>
                    (!cat.firstPref || cat.firstPref.length === 0) &&
                    (!cat.secondPref || cat.secondPref.length === 0)
                  ) ? 'Some categories need attention' : 'All categories active'}
                </div>
              </div>

              {/* Trending Categories */}
              <div className="panel tile">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <h4 style={{ fontSize: 14, fontWeight: 700 }}>Trending Now</h4>
                  <span style={{
                    width: 32, height: 32, borderRadius: 'var(--radius-sm)', display: 'grid', placeItems: 'center',
                    background: 'rgba(124,58,237,0.14)', color: 'var(--accent)',
                  }}>
                    <FaChartPie size={14} />
                  </span>
                </div>
                <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {categories
                    .sort((a, b) => {
                      const aFirst = a.firstPref.length;
                      const bFirst = b.firstPref.length;
                      return bFirst - aFirst;
                    })
                    .slice(0, 3)
                    .map((cat, index) => {
                      const dotColor = index === 0 ? 'var(--link)' : index === 1 ? 'var(--accent)' : 'var(--success)';
                      const pillClass = index === 0 ? 'pill pill-info' : index === 2 ? 'pill pill-success' : 'pill';
                      const pillStyle = index === 1 ? { background: 'rgba(124,58,237,.15)', color: 'var(--accent)' } : undefined;
                      const percentage = Math.round((cat.firstPref.length / totalStudents) * 100);

                      return (
                        <li key={cat.id} className="hero-list-row">
                          <span style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                            <span style={{ width: 6, height: 6, borderRadius: 999, background: dotColor, flexShrink: 0 }} />
                            <span className="truncate name" title={cat.name}>{cat.name}</span>
                          </span>
                          <span className={pillClass} style={pillStyle}>
                            {percentage}% ({cat.firstPref.length} First Pref.)
                          </span>
                        </li>
                      );
                    })}
                </ul>
              </div>
            </div>

            {/* Category Sections */}
            {categories.length === 0 ? (
              <div className="panel empty-state">
                <div className="empty-icon"><FaLayerGroup size={18} /></div>
                <p>No category selections yet.</p>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-5)' }}>
                {categories.map((category) => (
                  <div key={category.id} className="panel" style={{ padding: 'var(--sp-4) var(--sp-4) var(--sp-5)' }}>
                    <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3" style={{ marginBottom: 'var(--sp-4)' }}>
                      <h3 className="break-words" style={{ fontSize: 15, fontWeight: 700 }}>{category.name}</h3>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-3)' }}>
                        <span style={{ fontSize: 12, color: 'var(--text-3)' }}>{category.stats}</span>
                        <button
                          onClick={() => exportToExcel(category)}
                          className="btn btn-ghost btn-sm"
                          title="Export to Excel"
                        >
                          <FaFileExcel size={13} />
                          <span>Export</span>
                        </button>
                      </div>
                    </div>

                    <div className="flex flex-col lg:flex-row gap-4 md:gap-6">
                      {/* First Preference Column */}
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ borderBottom: '1px solid var(--border)', paddingBottom: 8, marginBottom: 'var(--sp-4)' }}>
                          <h4 style={{ fontSize: 13, fontWeight: 700, color: 'var(--success)' }}>
                            First Preference | {category.name.split('(')[0].trim()}
                          </h4>
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-3)' }}>
                          {category.firstPref.length > 0 ? (
                            category.firstPref.map((student, index) => (
                              <div
                                key={`first-${index}`}
                                style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', padding: 'var(--sp-3)' }}
                              >
                                <div className="flex flex-col sm:flex-row sm:items-center sm:space-x-3 space-y-2 sm:space-y-0">
                                  <div style={{
                                    width: 36, height: 36, borderRadius: '50%', flexShrink: 0,
                                    background: 'var(--surface-3)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-1)',
                                  }}>
                                    <FaUserPlus size={13} />
                                  </div>
                                  <div className="w-full" style={{ flex: 1, minWidth: 0 }}>
                                    <div style={{ fontWeight: 600, color: 'var(--text-1)' }}>{student.name} | {student.student_id}</div>
                                    <div style={{ fontSize: 11.5, color: 'var(--text-3)' }}>{student.faculty} | {student.year}</div>
                                    {student.secondPref && (
                                      <div style={{ fontSize: 11.5, color: 'var(--accent)', marginTop: 4 }}>2nd: {student.secondPref}</div>
                                    )}
                                  </div>
                                  <div style={{ fontSize: 12.5, color: 'var(--text-2)', marginTop: 4, display: 'flex', alignItems: 'center' }}>
                                    {student.phone}
                                    <button
                                      type="button"
                                      className="icon-btn"
                                      style={{ width: 24, height: 24, marginLeft: 6 }}
                                      aria-label={`Linked profile for ${student.name || 'student'}`}
                                    >
                                      <FaLink size={11} />
                                    </button>
                                  </div>
                                </div>
                                <div style={{ fontSize: 11, color: 'var(--text-3)', marginTop: 8 }}>
                                  Submitted on: {student.created_at ? new Date(student.created_at).toLocaleString('en-US', {
                                    day: '2-digit',
                                    month: 'short',
                                    year: 'numeric',
                                    hour: '2-digit',
                                    minute: '2-digit',
                                    hour12: true
                                  }) : 'Date not available'}
                                </div>
                              </div>
                            ))
                          ) : (
                            <div className="empty-state" style={{ padding: 'var(--sp-6) var(--sp-4)' }}>
                              <p>No first preferences yet</p>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Second Preference Column */}
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ borderBottom: '1px solid var(--border)', paddingBottom: 8, marginBottom: 'var(--sp-4)' }}>
                          <h4 style={{ fontSize: 13, fontWeight: 700, color: 'var(--accent)' }}>
                            Second Preference | {category.name.split('(')[0].trim()}
                          </h4>
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-3)' }}>
                          {category.secondPref.length > 0 ? (
                            category.secondPref.map((student, index) => (
                              <div
                                key={`second-${index}`}
                                style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', padding: 'var(--sp-3)' }}
                              >
                                <div className="flex flex-col sm:flex-row sm:items-center sm:space-x-3 space-y-2 sm:space-y-0">
                                  <div style={{
                                    width: 36, height: 36, borderRadius: '50%', flexShrink: 0,
                                    background: 'var(--surface-3)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-1)',
                                  }}>
                                    <FaUserPlus size={13} />
                                  </div>
                                  <div className="w-full" style={{ flex: 1, minWidth: 0 }}>
                                    <div style={{ fontWeight: 600, color: 'var(--text-1)' }}>{student.name} | {student.student_id}</div>
                                    <div style={{ fontSize: 11.5, color: 'var(--text-3)' }}>{student.faculty} | {student.year}</div>
                                    {student.firstPref && (
                                      <div style={{ fontSize: 11.5, color: 'var(--success)', marginTop: 4 }}>1st: {student.firstPref}</div>
                                    )}
                                  </div>
                                  <div style={{ fontSize: 12.5, color: 'var(--text-2)', marginTop: 4, display: 'flex', alignItems: 'center' }}>
                                    {student.phone}
                                    <button
                                      type="button"
                                      className="icon-btn"
                                      style={{ width: 24, height: 24, marginLeft: 6 }}
                                      aria-label={`Linked profile for ${student.name || 'student'}`}
                                    >
                                      <FaLink size={11} />
                                    </button>
                                  </div>
                                </div>
                              </div>
                            ))
                          ) : (
                            <div className="empty-state" style={{ padding: 'var(--sp-6) var(--sp-4)' }}>
                              <p>No second preferences yet</p>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </main>
    </div>
  );
}
